import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import CustomerLayout from './CustomerLayout';
import SafeIcon from '../../common/SafeIcon';
import supabase from '../../supabase/supabase';
import { Button } from '../../components/ui/Button';
import { useLoadScript, Autocomplete } from '@react-google-maps/api';
import { ALL_CITIES, getDistrictForCity } from '../../utils/locations';
import Toast from '../../components/ui/Toast';
import { useToast } from '../../hooks/useToast';

const libraries = ['places'];

export default function CustomerProfile() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState('main');
  const [savedAddresses, setSavedAddresses] = useState([]);
  
  const [editCity, setEditCity] = useState('');
  const [citySearch, setCitySearch] = useState('');
  const [showCityDropdown, setShowCityDropdown] = useState(false);
  
  const [savingLocation, setSavingLocation] = useState(false);
  const [newTitle, setNewTitle] = useState('Home');
  const [newAddress, setNewAddress] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [editingAddressIndex, setEditingAddressIndex] = useState(null);
  
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  const { toast, showToast, hideToast } = useToast();

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries
  });
  const [autocomplete, setAutocomplete] = useState(null);

  const filteredCities = ALL_CITIES.filter(c => c.toLowerCase().includes(citySearch.toLowerCase()));

  useEffect(() => {
    const fetchProfile = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        const { data } = await supabase.from('profiles_1740423033529').select('*').eq('id', user.id).single();
        setProfile({ ...data, email: user.email });
        
        const currentCity = data?.city || '';
        setEditCity(currentCity);
        setCitySearch(currentCity);
        
        let addresses = data?.saved_addresses || [];
        if (addresses.length === 0 && data?.address) {
          addresses = [{ title: 'Main', address: data.address }];
        }
        setSavedAddresses(addresses);
      }
      setLoading(false);
    };
    fetchProfile();
  }, []);

  const handleAvatarClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    fileInputRef.current?.click();
  };

  const compressImage = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const MAX_WIDTH = 800;
        let width = img.width, height = img.height;
        if (width > MAX_WIDTH || height > MAX_WIDTH) {
          if (width > height) {
            height = Math.round(height * MAX_WIDTH / width);
            width = MAX_WIDTH;
          } else {
            width = Math.round(width * MAX_WIDTH / height);
            height = MAX_WIDTH;
          }
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        canvas.getContext('2d').drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };
      img.onerror = reject;
    };
    reader.onerror = reject;
  });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      try {
        const compressed = await compressImage(file);
        setProfile(prev => ({ ...prev, avatar_url: compressed }));
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('profiles_1740423033529').update({ avatar_url: compressed, updated_at: new Date().toISOString() }).eq('id', user.id);
        }
      } catch {
        showToast("Failed to process image.", "error");
      }
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'CONFIRM') return;
    setDeleting(true);
    try {
      const { error } = await supabase.rpc('delete_own_account');
      if (error) throw error;
      await supabase.auth.signOut();
      navigate('/login', { replace: true });
    } catch (err) {
      showToast("Failed to delete account: " + err.message, "error");
      setDeleting(false);
    }
  };

  const saveLocation = async () => {
    setSavingLocation(true);
    const district = getDistrictForCity(editCity);
    
    const { error } = await supabase.from('profiles_1740423033529').update({
      saved_addresses: savedAddresses,
      city: editCity,
      district: district || null,
      address: savedAddresses.length > 0 ? savedAddresses[0].address : '',
      updated_at: new Date().toISOString()
    }).eq('id', profile.id);

    if (!error) {
      setProfile(prev => ({ ...prev, saved_addresses: savedAddresses, city: editCity, district }));
      showToast("Locations saved successfully.", "success");
    } else {
      showToast("Error saving location: " + error.message, "error");
    }
    setSavingLocation(false);
  };

  const handleEditAddress = (index) => {
    setEditingAddressIndex(index);
    setNewTitle(savedAddresses[index].title);
    
    const addrStr = savedAddresses[index].address;
    const notesMatch = addrStr.match(/\(Notes: (.*?)\)$/);
    if (notesMatch) {
      setNewNotes(notesMatch[1]);
      setNewAddress(addrStr.replace(/\s*\(Notes: .*?\)$/, ''));
    } else {
      setNewNotes('');
      setNewAddress(addrStr);
    }
  };

  const handleCreateOrUpdateAddress = () => {
    if (newTitle && newAddress) {
      const finalAddress = newNotes.trim() ? `${newAddress.trim()} (Notes: ${newNotes.trim()})` : newAddress.trim();
      
      if (editingAddressIndex !== null) {
        const updated = [...savedAddresses];
        updated[editingAddressIndex] = { title: newTitle, address: finalAddress };
        setSavedAddresses(updated);
        setEditingAddressIndex(null);
      } else {
        setSavedAddresses([...savedAddresses, { title: newTitle, address: finalAddress }]);
      }
      
      setNewTitle('');
      setNewAddress('');
      setNewNotes('');
    } else {
      showToast("Please provide both a title and an address.", "error");
    }
  };

  const onLoadGoogleMaps = (autoC) => setAutocomplete(autoC);
  const onPlaceChanged = () => {
    if (autocomplete !== null) {
      const place = autocomplete.getPlace();
      if (place.formatted_address) setNewAddress(place.formatted_address);
    }
  };

  const menuItems = [
    { icon: 'User', label: 'Personal Information', view: 'personal' },
    { icon: 'CreditCard', label: 'Payment Methods', view: 'payment' },
    { icon: 'MapPin', label: 'My City & Addresses', view: 'addresses' },
    { icon: 'Bell', label: 'Notifications', view: 'notifications' },
    { icon: 'Shield', label: 'Privacy & Security', view: 'security' },
    { icon: 'HelpCircle', label: 'Help Center', action: () => window.location.href = 'mailto:help@bookotg.com?subject=Support Request - BookOTG' },
  ];

  if (loading) return <CustomerLayout><div className="p-12 text-center">Loading Profile...</div></CustomerLayout>;

  const Header = ({ title }) => (
    <div className="flex items-center gap-4 mb-8">
      <button onClick={() => setActiveView('main')} className="p-2 bg-white rounded-full shadow-sm hover:bg-gray-50">
        <SafeIcon name="ArrowLeft" />
      </button>
      <h2 className="text-xl font-black text-brand-dark">{title}</h2>
    </div>
  );

  const renderSubView = () => {
    if (activeView === 'personal') return (
      <div className="p-6 animate-in slide-in-from-right-4">
        <Header title="Personal Information" />
        <div className="space-y-4">
          {[{ label: 'Full Name', value: profile?.full_name }, { label: 'Email', value: profile?.email }, { label: 'Phone', value: profile?.phone || 'Not provided' }, { label: 'City', value: profile?.city || 'Not set' }, { label: 'District', value: profile?.district || 'Not set' }].map((item, i) => (
            <div key={i} className="p-4 bg-white rounded-2xl border border-gray-100">
              <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">{item.label}</label>
              <p className="font-bold text-brand-dark mt-1">{item.value}</p>
            </div>
          ))}
        </div>
      </div>
    );

    if (activeView === 'payment') return (
      <div className="p-6 animate-in slide-in-from-right-4">
        <Header title="Payment Methods" />
        <div className="bg-white p-8 rounded-3xl border border-dashed border-gray-200 text-center mt-10">
          <div className="w-16 h-16 bg-blue-50 text-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon name="CreditCard" className="text-2xl" />
          </div>
          <h3 className="text-lg font-black text-brand-dark mb-2">Card Payments Coming Soon</h3>
          <p className="text-sm text-gray-500">Currently all services are paid in cash after grooming.</p>
        </div>
      </div>
    );

    if (activeView === 'addresses') return (
      <div className="p-6 animate-in slide-in-from-right-4">
        <Header title="My City & Addresses" />
        <div className="bg-white p-6 rounded-3xl border border-gray-100 shadow-sm space-y-6">
          {/* City selector */}
          <div>
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">Your City</label>
            <div className="relative">
              <SafeIcon name="Search" className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 z-10" />
              <input 
                type="text" 
                placeholder="Search your city..."
                value={citySearch}
                onChange={(e) => {
                  setCitySearch(e.target.value);
                  setEditCity('');
                  setShowCityDropdown(true);
                }}
                onFocus={() => setShowCityDropdown(true)}
                className="w-full pl-12 pr-10 py-4 bg-gray-50 border border-gray-200 rounded-2xl outline-none focus:border-brand-primary font-bold text-brand-dark"
              />
              {editCity && <div className="absolute right-4 top-1/2 -translate-y-1/2"><SafeIcon name="CheckCircle" className="text-green-500" /></div>}
            </div>

            {editCity && (
              <p className="text-[10px] text-brand-primary font-black uppercase tracking-widest mt-1 pl-1">
                District: {getDistrictForCity(editCity)}
              </p>
            )}

            {showCityDropdown && !editCity && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowCityDropdown(false)}></div>
                <div className="absolute z-50 w-full mt-2 bg-white border border-gray-100 rounded-2xl shadow-xl max-h-56 overflow-y-auto py-2" style={{left: 0}}>
                  {filteredCities.length > 0 ? filteredCities.map(c => (
                    <div 
                      key={c} 
                      className="px-5 py-3 hover:bg-brand-primary/5 cursor-pointer font-bold text-brand-dark text-sm transition-colors flex justify-between items-center"
                      onClick={() => {
                        setEditCity(c);
                        setCitySearch(c);
                        setShowCityDropdown(false);
                      }}
                    >
                      <span>{c}</span>
                      <span className="text-[10px] text-gray-400">{getDistrictForCity(c)}</span>
                    </div>
                  )) : <div className="px-5 py-3 text-gray-400 text-sm italic">No cities found</div>}
                </div>
              </>
            )}
          </div>

          {/* Addresses */}
          <div className="space-y-4">
            <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest">Saved Addresses</label>
            
            {savedAddresses.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No addresses saved yet.</p>
            ) : savedAddresses.map((addr, index) => (
              <div key={index} className="flex justify-between items-center bg-gray-50 p-4 rounded-xl border border-gray-100">
                <div className="pr-4">
                  <p className="font-bold text-brand-dark text-sm">{addr.title}</p>
                  <p className="text-xs text-gray-500 mt-1 leading-relaxed">{addr.address}</p>
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  <button onClick={() => handleEditAddress(index)} className="text-brand-primary p-2 hover:bg-brand-primary/10 rounded-lg">
                    <SafeIcon name="Edit2" className="text-sm" />
                  </button>
                  <button onClick={() => setSavedAddresses(savedAddresses.filter((_, i) => i !== index))} className="text-red-500 p-2 hover:bg-red-50 rounded-lg">
                    <SafeIcon name="Trash2" className="text-sm" />
                  </button>
                </div>
              </div>
            ))}

            <div className="pt-4 border-t border-gray-100">
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                {editingAddressIndex !== null ? 'Update Location' : 'Add New Location'}
              </label>
              <div className="flex gap-2 mb-3">
                <input 
                  type="text" 
                  placeholder="Title (e.g. Home)"
                  value={newTitle}
                  onChange={e => setNewTitle(e.target.value)}
                  className="w-1/3 p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary text-sm font-bold"
                />
                <div className="flex-1">
                  {isLoaded ? (
                    <Autocomplete onLoad={onLoadGoogleMaps} onPlaceChanged={onPlaceChanged}>
                      <input 
                        type="text" 
                        placeholder="Search Address..."
                        value={newAddress}
                        onChange={e => setNewAddress(e.target.value)}
                        className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary text-sm font-medium"
                      />
                    </Autocomplete>
                  ) : (
                    <input 
                      type="text" 
                      placeholder="Full Address..."
                      value={newAddress}
                      onChange={e => setNewAddress(e.target.value)}
                      className="w-full p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary text-sm font-medium"
                    />
                  )}
                </div>
              </div>
              <input 
                type="text" 
                placeholder="Unit/floor/extra directions (optional)"
                value={newNotes}
                onChange={e => setNewNotes(e.target.value)}
                className="w-full mb-3 p-3 bg-gray-50 border border-gray-200 rounded-xl outline-none focus:border-brand-primary text-sm font-medium"
              />
              <div className="flex gap-2">
                {editingAddressIndex !== null && (
                  <Button variant="ghost" size="sm" className="flex-1" onClick={() => {
                    setEditingAddressIndex(null); setNewTitle(''); setNewAddress(''); setNewNotes('');
                  }}>Cancel</Button>
                )}
                <Button variant="secondary" size="sm" className="flex-[2]" onClick={handleCreateOrUpdateAddress}>
                  <SafeIcon name={editingAddressIndex !== null ? "Save" : "Plus"} className="mr-2" />
                  {editingAddressIndex !== null ? 'Update' : 'Add Location'}
                </Button>
              </div>
            </div>
          </div>

          <Button className="w-full mt-4" onClick={saveLocation} disabled={savingLocation}>
            {savingLocation ? 'Saving...' : 'Save All Changes'}
          </Button>
        </div>
      </div>
    );

    if (activeView === 'notifications') return (
      <div className="p-6 animate-in slide-in-from-right-4">
        <Header title="Notifications" />
        <div className="bg-white rounded-3xl border border-gray-100 divide-y divide-gray-50">
          <div className="p-5 flex justify-between items-center">
            <div><p className="font-bold text-brand-dark">Email Updates</p><p className="text-xs text-gray-400">Booking confirmations & receipts</p></div>
            <div className="w-12 h-6 bg-brand-primary rounded-full relative"><div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full"></div></div>
          </div>
          <div className="p-5 flex justify-between items-center">
            <div><p className="font-bold text-brand-dark">Promotional Offers</p><p className="text-xs text-gray-400">Discounts and new barbers</p></div>
            <div className="w-12 h-6 bg-gray-200 rounded-full relative"><div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full"></div></div>
          </div>
        </div>
      </div>
    );

    if (activeView === 'security') return (
      <div className="p-6 animate-in slide-in-from-right-4">
        <Header title="Privacy & Security" />
        <div className="space-y-4">
          <button className="w-full bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between text-left hover:border-brand-primary/30 transition-colors">
            <div><p className="font-bold text-brand-dark">Change Password</p><p className="text-xs text-gray-400 mt-1">Update your account credentials</p></div>
            <SafeIcon name="ChevronRight" className="text-gray-300" />
          </button>
          <button onClick={() => setShowDeleteModal(true)} className="w-full bg-red-50 p-5 rounded-2xl border border-red-100 shadow-sm flex items-center justify-between text-left hover:bg-red-100 transition-colors mt-8">
            <div><p className="font-bold text-red-600">Delete Account</p><p className="text-xs text-red-400 mt-1">Permanently remove your data</p></div>
            <SafeIcon name="Trash2" className="text-red-500" />
          </button>
        </div>

        {showDeleteModal && (
          <div className="fixed inset-0 bg-brand-dark/60 z-50 flex items-center justify-center p-6 backdrop-blur-sm">
            <div className="bg-white rounded-[2rem] p-8 max-w-sm w-full text-center shadow-2xl animate-in zoom-in-95">
              <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon name="AlertTriangle" className="text-3xl" />
              </div>
              <h2 className="text-xl font-black text-brand-dark mb-2">Delete Account?</h2>
              <p className="text-gray-500 font-medium text-sm mb-6 text-center leading-relaxed">
                Type <strong className="text-red-600">CONFIRM</strong> to permanently delete your account.
              </p>
              <input 
                type="text" 
                placeholder="Type CONFIRM"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl mb-6 text-center font-black outline-none focus:border-red-500"
              />
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => {setShowDeleteModal(false); setDeleteConfirmText('');}}>Cancel</Button>
                <Button variant="danger" className="flex-1" disabled={deleteConfirmText !== 'CONFIRM' || deleting} onClick={handleDeleteAccount}>
                  {deleting ? 'Deleting...' : 'Delete'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    return null;
  };

  return (
    <CustomerLayout>
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      {activeView !== 'main' ? renderSubView() : (
        <div className="p-6 animate-in fade-in">
          <div className="flex flex-col items-center mb-8">
            <div className="relative">
              <input type="file" ref={fileInputRef} className="hidden" accept="image/*" onChange={handleFileChange} />
              <img src={profile?.avatar_url || `https://ui-avatars.com/api/?name=${profile?.full_name || 'User'}&background=0047FF&color=fff`} className="w-24 h-24 rounded-full border-4 border-white shadow-xl object-cover bg-brand-light" alt="Profile" />
              <button 
                onClick={handleAvatarClick}
                className="absolute bottom-0 right-0 w-8 h-8 bg-brand-primary rounded-full flex items-center justify-center text-white border-2 border-white shadow-lg active:scale-90 transition-transform"
              >
                <SafeIcon name="Camera" className="text-xs" />
              </button>
            </div>
            <h2 className="text-2xl font-black mt-4 text-brand-dark tracking-tight">{profile?.full_name}</h2>
            <p className="text-gray-500 text-sm font-medium">{profile?.email}</p>
            {profile?.city && (
              <div className="mt-2 flex items-center gap-1 text-xs text-brand-primary font-bold">
                <SafeIcon name="MapPin" className="text-xs" /> {profile.city}{profile.district ? `, ${profile.district}` : ''}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              <span className="px-3 py-1 bg-brand-primary/10 text-brand-primary text-[10px] font-black uppercase rounded-full border border-brand-primary/10">Customer Account</span>
              <span className="px-3 py-1 bg-green-50 text-green-600 text-[10px] font-black uppercase rounded-full border border-green-100">Verified</span>
            </div>
          </div>

          <div className="space-y-3">
            {menuItems.map((item, i) => (
              <button 
                key={i} 
                className="w-full flex items-center justify-between p-4 bg-white rounded-2xl border border-gray-100 active:scale-[0.98] transition-all hover:bg-gray-50 hover:border-brand-primary/20 shadow-sm group"
                onClick={item.action || (() => setActiveView(item.view))}
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-50 rounded-xl flex items-center justify-center text-gray-400 group-hover:bg-brand-primary/10 group-hover:text-brand-primary transition-colors">
                    <SafeIcon name={item.icon} />
                  </div>
                  <span className="font-bold text-brand-dark">{item.label}</span>
                </div>
                <SafeIcon name="ChevronRight" className="text-gray-300 group-hover:text-brand-primary group-hover:translate-x-1 transition-all" />
              </button>
            ))}
          </div>

          <button 
            className="w-full mt-8 flex items-center justify-center gap-2 p-5 bg-red-50 text-red-600 font-black text-sm rounded-2xl border border-red-100 active:scale-95 transition-transform"
            onClick={handleLogout}
          >
            <SafeIcon name="LogOut" /> Sign Out of BookOTG
          </button>
          
          <p className="mt-8 text-center text-[10px] text-gray-400 font-bold uppercase tracking-widest">Version 1.0.3 • BookOTG Sri Lanka</p>
        </div>
      )}
    </CustomerLayout>
  );
}